import { useEffect, useState } from 'react';
import type { ExternoResponse, SalvarExternosDto } from '@triade/contracts';
import { Plus, Trash2 } from 'lucide-react';
import { useExternos, useSalvarExternos, type EntidadeExterna } from '@/api/externos';
import { ErroLista } from '@/components/cargos';
import { ApiError } from '@/lib/api';

const CAMPO = 'h-[34px] w-full rounded-[9px] border border-input bg-card px-2.5 text-small text-ink-body focus-visible:border-primary focus-visible:shadow-focus focus-visible:outline-none disabled:opacity-70';

/** Linha da tela: `importado` = veio de importacao (so leitura, nao vai no PUT). */
interface LinhaExterna {
  chave: number;
  sistema: string;
  colecao: string;
  idExterno: string;
  importado: boolean;
}

const mensagemApi = (e: unknown) => (e instanceof ApiError ? `${e.message}${e.code ? ` (${e.code})` : ''}` : 'falha de comunicação com a API');

/** Codigos externos (ex.: Protheus) de um cadastro salvo. Sem `editavel` a lista e so leitura. `colecaoFixa`: tipo do codigo fixo (ex.: clientes), sem coluna na tela. Nada e calculado aqui. */
export function CodigosExternos({ entidade, id, editavel, colecaoFixa }: { entidade: EntidadeExterna; id: string; editavel: boolean; colecaoFixa?: string }) {
  const consulta = useExternos(entidade, id);
  const salvar = useSalvarExternos();
  const [linhas, setLinhas] = useState<LinhaExterna[]>([]);
  const [proximaChave, setProximaChave] = useState(1);
  const [erro, setErro] = useState<string | null>(null);
  const [salvoEm, setSalvoEm] = useState(false);
  const [sujo, setSujo] = useState(false);

  useEffect(() => {
    if (!consulta.data) return;
    setLinhas(consulta.data.map((r, i) => ({ chave: i + 1, sistema: r.sistema, colecao: r.colecao, idExterno: r.idExterno, importado: r.importado })));
    setProximaChave(consulta.data.length + 1);
    setSujo(false);
  }, [consulta.data]);

  function alterar(chave: number, campo: 'sistema' | 'colecao' | 'idExterno', valor: string) {
    setLinhas((atual) => atual.map((l) => (l.chave === chave ? { ...l, [campo]: valor } : l)));
    setSujo(true);
    setSalvoEm(false);
  }

  function adicionar() {
    setLinhas((atual) => [...atual, { chave: proximaChave, sistema: '', colecao: colecaoFixa ?? '', idExterno: '', importado: false }]);
    setProximaChave((k) => k + 1);
    setSujo(true);
  }

  function remover(chave: number) {
    setLinhas((atual) => atual.filter((l) => l.chave !== chave));
    setSujo(true);
  }

  async function gravar() {
    setErro(null);
    // Somente os codigos nao importados vao no PUT (os importados seguem como estao na API).
    const refs = linhas
      .filter((l) => !l.importado)
      .map((l) => ({ sistema: l.sistema.trim(), colecao: (colecaoFixa ?? l.colecao).trim(), idExterno: l.idExterno.trim() }));
    const incompleta = refs.find((r) => !r.sistema || !r.colecao || !r.idExterno);
    if (incompleta) {
      setErro('Preencha sistema, tipo do código e código em todas as linhas.');
      return;
    }
    const dto: SalvarExternosDto = { refs };
    try {
      const gravados: ExternoResponse[] = await salvar.mutateAsync({ entidade, id, dto });
      setLinhas(gravados.map((r, i) => ({ chave: i + 1, sistema: r.sistema, colecao: r.colecao, idExterno: r.idExterno, importado: r.importado })));
      setProximaChave(gravados.length + 1);
      setSujo(false);
      setSalvoEm(true);
    } catch (e) {
      setErro(mensagemApi(e));
    }
  }

  if (consulta.isError && !consulta.data) {
    return <ErroLista erro={consulta.error} onTentar={() => void consulta.refetch()} titulo="Não foi possível carregar os códigos externos" />;
  }
  if (!consulta.data) return <p role="status" className="text-small text-ink-muted">Carregando códigos externos…</p>;

  return (
    <section aria-label="Códigos externos" className="flex flex-col gap-3">
      <h3 className="text-small font-semibold">Códigos externos</h3>
      {linhas.length === 0 && <p className="text-small text-ink-muted">Nenhum código externo cadastrado.</p>}
      {linhas.length > 0 && (
        <table className="w-full border-collapse text-small">
          <thead>
            <tr className="text-left text-caption text-ink-muted">
              <th scope="col" className="px-2 py-2 font-medium">Sistema</th>
              {!colecaoFixa && <th scope="col" className="px-2 py-2 font-medium">Tipo do código</th>}
              <th scope="col" className="px-2 py-2 font-medium">Código</th>
              <th scope="col" aria-label="Remover" className="w-10 px-2 py-2 font-medium" />
            </tr>
          </thead>
          <tbody>
            {linhas.map((l) => (
              <tr key={l.chave} data-testid="linha-externo" className="border-t border-line-soft">
                {l.importado ? (
                  <>
                    <td className="px-2 py-2">{l.sistema}</td>
                    {!colecaoFixa && <td className="px-2 py-2">{l.colecao}</td>}
                    <td className="px-2 py-2 font-mono">
                      {l.idExterno} <span className="ml-1 rounded-full bg-line-soft px-2 py-0.5 text-caption text-ink-secondary">importado</span>
                    </td>
                    <td />
                  </>
                ) : (
                  <>
                    <td className="px-2 py-2">
                      <input aria-label="Sistema" disabled={!editavel} maxLength={50} className={CAMPO} value={l.sistema} onChange={(e) => alterar(l.chave, 'sistema', e.target.value)} />
                    </td>
                    {!colecaoFixa && (
                      <td className="px-2 py-2">
                        <input aria-label="Tipo do código" disabled={!editavel} maxLength={50} className={CAMPO} value={l.colecao} onChange={(e) => alterar(l.chave, 'colecao', e.target.value)} />
                      </td>
                    )}
                    <td className="px-2 py-2">
                      <input aria-label="Código" disabled={!editavel} maxLength={100} className={`${CAMPO} font-mono`} value={l.idExterno} onChange={(e) => alterar(l.chave, 'idExterno', e.target.value)} />
                    </td>
                    <td className="px-2 py-2">
                      {editavel && (
                        <button type="button" aria-label="Remover código" onClick={() => remover(l.chave)} className="flex size-8 items-center justify-center rounded-md text-ink-secondary hover:bg-line-soft">
                          <Trash2 aria-hidden="true" className="size-4" />
                        </button>
                      )}
                    </td>
                  </>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {editavel && (
        <div className="flex flex-wrap items-center gap-3">
          <button type="button" onClick={adicionar} className="flex h-[34px] items-center gap-1.5 rounded-[9px] border border-border bg-card px-3 text-small font-medium hover:bg-line-soft">
            <Plus aria-hidden="true" className="size-[15px]" />
            Adicionar código
          </button>
          <button type="button" onClick={() => void gravar()} disabled={!sujo || salvar.isPending} className="h-[34px] rounded-[9px] bg-primary px-4 text-small font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-60">
            {salvar.isPending ? 'Gravando…' : 'Gravar códigos'}
          </button>
          {salvoEm && !sujo && <span role="status" className="text-caption text-ink-secondary">Códigos gravados.</span>}
        </div>
      )}
      {erro && (
        <p role="alert" className="rounded-md bg-destructive/10 px-3 py-2 text-small text-danger-ink">
          {erro}
        </p>
      )}
      {!editavel && <p className="text-caption text-ink-muted">Somente leitura: sem permissão para editar os códigos externos.</p>}
    </section>
  );
}
